import { User } from "firebase/auth";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link, Outlet, useOutletContext } from "react-router-dom";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import PageLoader from "../components/PageLoader";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import StatusBadge from "../components/admin/shared/StatusBadge";
import AdminSubNav from "../components/admin/shared/AdminSubNav";
import SquadTab from "../components/admin/competition/SquadTab";
import ModesTab from "../components/admin/competition/ModesTab";
import OverviewTab from "../components/admin/competition/OverviewTab";
import ClubsTab from "../components/admin/competition/ClubsTab";
import AutomationTab from "../components/admin/competition/AutomationTab";
import GameweeksTab from "../components/admin/competition/GameweeksTab";
import GameweekOpsPage from "../components/admin/competition/GameweekOpsPage";
import {
  fetchAdminCompetition, fetchAutomationConfig,
  AdminCompetition, CompetitionAutomationConfig,
} from "../api/admin";
import { Club, getAllClubs } from "../api/clubs";
import { competitionModeLabel } from "../lib/admin/competitionModes";
import {
  widePageContainer,
  errorStyle, card, badge,
} from "../styles";

interface Props {
  user: User;
}

interface CompetitionOutletContext {
  user: User;
  comp: AdminCompetition;
  reload: () => Promise<void>;
  allClubs: Club[];
  automationConfig: CompetitionAutomationConfig | null;
  setAutomationConfig: (cfg: CompetitionAutomationConfig | null) => void;
}

function useCompetitionOutletContext() {
  return useOutletContext<CompetitionOutletContext>();
}

// ─── Nested-route adapters ────────────────────────────────────────────────────
// Each existing tab component keeps its exact prop signature — these just read
// what they need off the parent route's Outlet context instead of AdminCompetitionPage
// passing props directly, since each is now mounted at its own URL, not a tab.

export function OverviewRoute() {
  const { user, comp, reload } = useCompetitionOutletContext();
  return <OverviewTab user={user} comp={comp} reload={reload} />;
}

export function GameweeksRoute() {
  const { user, comp, reload } = useCompetitionOutletContext();
  return <GameweeksTab user={user} comp={comp} reload={reload} />;
}

export function GameweekOpsRoute() {
  const { user, comp, reload } = useCompetitionOutletContext();
  return <GameweekOpsPage user={user} competitionId={comp.id} reload={reload} />;
}

export function EntrantsRoute() {
  const { user, comp, reload, allClubs } = useCompetitionOutletContext();
  return <ClubsTab user={user} comp={comp} reload={reload} allClubs={allClubs} />;
}

export function SquadsRoute() {
  const { user, comp } = useCompetitionOutletContext();
  return <SquadTab user={user} comp={comp} />;
}

export function DataSourceRoute() {
  const { user, comp, reload, allClubs, automationConfig, setAutomationConfig } = useCompetitionOutletContext();
  return (
    <AutomationTab
      user={user} comp={comp} reload={reload} allClubs={allClubs}
      automationConfig={automationConfig} onAutomationConfigChange={setAutomationConfig}
    />
  );
}

export function ModesRoute() {
  const { user, comp, reload } = useCompetitionOutletContext();
  return <ModesTab user={user} comp={comp} reload={reload} />;
}

// ─── Shell ────────────────────────────────────────────────────────────────────

export default function AdminCompetitionPage({ user }: Props) {
  const { id } = useParams<{ id: string }>();
  const [comp, setComp] = useState<AdminCompetition | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");
  const delayedLoading = useDelayedLoading(pageLoading);

  const [allClubs, setAllClubs] = useState<Club[]>([]);
  const [automationConfig, setAutomationConfig] = useState<CompetitionAutomationConfig | null>(null);

  useBreadcrumbs(
    comp
      ? [{ label: "Admin", to: "/admin" }, { label: comp.name }]
      : [{ label: "Admin", to: "/admin" }, { label: "Competition" }],
  );

  const loadComp = async () => {
    if (!id) return;
    try {
      const data = await fetchAdminCompetition(user, id);
      setComp(data);
    } catch {
      setError("Failed to load competition");
    } finally {
      setPageLoading(false);
    }
    try {
      const cfg = await fetchAutomationConfig(user, id);
      setAutomationConfig(cfg);
    } catch {
      setAutomationConfig(null);
    }
  };

  useEffect(() => {
    loadComp();
    getAllClubs(user).then(setAllClubs).catch(() => {});
  }, [id]); // eslint-disable-line

  const { t } = useTranslation();

  if (delayedLoading) return <PageLoader />;
  if (pageLoading) return null;
  if (!comp) return <div style={{ padding: "2rem", color: "var(--color-danger)" }}>{error || "Competition not found"}</div>;

  const subNavItems = [
    { to: `/admin/competitions/${comp.id}`, label: t("admin.overview.tabLabel"), end: true },
    { to: `/admin/competitions/${comp.id}/gameweeks`, label: t("admin.competitions.gwList.title") },
    { to: `/admin/competitions/${comp.id}/entrants`, label: `Teams${comp.clubs.length > 0 ? ` (${comp.clubs.length})` : ""}` },
    { to: `/admin/competitions/${comp.id}/squads`, label: "Squad" },
    { to: `/admin/competitions/${comp.id}/data-source`, label: t("admin.fplAutomation.tabLabel") },
    { to: `/admin/competitions/${comp.id}/modes`, label: t("admin.modes.tabLabel") },
  ];

  const outletContext: CompetitionOutletContext = { user, comp, reload: loadComp, allClubs, automationConfig, setAutomationConfig };

  return (
    <div className="sg-fade-in" style={{ ...widePageContainer, padding: "1.5rem 0" }}>
      {/* ── Page header ── */}
      <div style={{ ...card, marginBottom: "1.25rem" }}>
        <div style={{ marginBottom: "0.75rem" }}>
          <Link to="/admin" style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", textDecoration: "none" }}>← Admin</Link>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.3rem", flexWrap: "wrap" }}>
              <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, color: "var(--color-text)" }}>
                {comp.name}
              </h1>
            </div>
            <div style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
              <span>{comp.season}</span>
              <span>·</span>
              <span>{comp.type === "CLUB" ? "Club" : "International"}</span>
              <span>·</span>
              <span>{comp.gameweeks.filter((g) => g.number > 0).length} gameweeks</span>
              <StatusBadge status={comp.status} />
              {comp.archived && (
                <span style={{ ...badge, background: "var(--color-surface-alt)", color: "var(--color-text-dim)" }}>{t("admin.competition.archived")}</span>
              )}
              <span>·</span>
              {comp.supportedModes.length > 0 ? (
                <span>{comp.supportedModes.map((m) => competitionModeLabel(m)).join(", ")}</span>
              ) : (
                <span style={{ color: "var(--color-text-dim)", fontStyle: "italic" }}>hidden from new leagues</span>
              )}
            </div>
          </div>
        </div>

        <AdminSubNav items={subNavItems} />
      </div>

      {error && <p style={{ ...errorStyle, padding: "0.5rem 1rem", marginBottom: "1rem" }}>{error}</p>}

      <Outlet context={outletContext} />
    </div>
  );
}
